import memoizeOne from "memoize-one";
import { useMemo } from "react";
import ColumnSelector from "../components/ColumnSelector";
import { MultiFieldEditor } from "../components/MultiFieldEditor";
import { PackEditorCallbacks } from "../core/EditorTypes";
import { getAllWires } from "../linked/Helpers";
import { Pack } from "../Types";
import { wireToString } from "../utils/WireUtils";

type Props = {
  goToIndex: () => void;
  pack: Pack;
  selectedCols: string[];
  setSelectedCols: (cols: string[]) => void;
  repCards: JSX.Element[];
  repPairs: [string, string, string][];
  repKeys: string[];
  packEditorCallbacks: PackEditorCallbacks;
};

export const PackConfigPage = (props: Props) => {
  const {
    goToIndex,
    pack,
    selectedCols,
    setSelectedCols,
    repKeys,
    repPairs,
    repCards,
    packEditorCallbacks: cb,
  } = props;

  const ttsConfig = pack.data.config?.tts || {
    enabled: false,
  };

  const allWires = useMemo(() => getAllWires(pack), [
    pack,
  ]);


  const allWireStrs = useMemo(
    () => allWires.map(wireToString),
    [allWires],
  );

  return (
    <>
      <div className="back-button padding" onClick={goToIndex}>
        ← <span className="underline">back to pack</span>
      </div>
      <div className="section">🔍 choose what to view</div>
      <div style={{ padding: "0 2em" }}>
        <ColumnSelector
          columns={[
            "text",
            // "html",
            // "markdown",
            "subtext",
            "audio",
            "video",
            "image",
            // "attachments",
            "text-to-speech",
            "templates",
            // "fields",
            // "controls",
            "examples",
          ]}
          selectedColumns={selectedCols}
          setSelected={setSelectedCols}
        />
      </div>
      <div className="section">🏛 representations</div>
      <div className="representations" style={{ backgroundColor: "#ccc" }}>
        {repCards}
      </div>
      <div
        className={`add-btn`}
        onClick={cb.addBaseRepCb}
        style={{ margin: "1em" }}
      >
        ➕ add representation
      </div>
      {selectedCols.includes("templates") && (
        <>
          <div className="section">📝 templates</div>
          <div className="templates">
            <MultiFieldEditor
              fields={pack.data.templates || {}}
              onRemoveField={cb.removeTemplate}
              onEditValue={cb.editTemplate}
              onEditKey={cb.renameTemplate}
            />
            <div
              className={`add-btn`}
              onClick={cb.addTemplate}
              style={{ margin: "1em" }}
            >
              ➕ add template
            </div>
          </div>
        </>
      )}
      {selectedCols.includes("fields") && (
        <>
          <div className="section">🎨 fields</div>
          <div className="fields">
            <MultiFieldEditor
              fields={pack.data.fields || {}}
              onRemoveField={cb.removePackField}
              onEditValue={cb.editPackField}
              onEditKey={cb.renamePackField}
            />
            <div
              className={`add-btn`}
              onClick={cb.addPackField}
              style={{ margin: "1em" }}
            >
              ➕ add field
            </div>
          </div>
        </>
      )}
      {selectedCols.includes("text-to-speech") && (
        <>
          <div className="section">🗣 text-to-speech</div>
          <div className="padding">
            <label style={{ fontWeight: "bold" }} htmlFor="tts-enabled">
              Enable
            </label>
            <input
              type="checkbox"
              value={"tts-enabled"}
              checked={ttsConfig.enabled}
              onChange={(e) => {
                cb.modifyPackConfig({
                  tts: {
                    enabled: e.target.checked,
                  },
                });
              }}
            />
            {ttsConfig.enabled && (
              <div>
                {repKeys.map((repKey) => {
                  const isOn = ttsConfig?.reps?.[repKey]?.enabled ?? true;
                  return (
                    <div className="row">
                      <label className="cell">
                        {pack.data.reps[repKey].promptText ?? repKey}
                      </label>
                      <input
                        className="cell"
                        type="checkbox"
                        value={repKey}
                        checked={isOn}
                        onChange={(e) => {
                          cb.modifyPackConfig({
                            tts: {
                              reps: {
                                [repKey]: {
                                  enabled: e.target.checked,
                                },
                              },
                            },
                          });
                        }}
                      />
                      <>
                        {/* text input that sets language */}
                        <label className="cell">Language code</label>
                        <input
                          className="cell"
                          type="text"
                          placeholder="en-US"
                          value={ttsConfig?.reps?.[repKey]?.language ?? ""}
                          onChange={(e) => {
                            cb.modifyPackConfig({
                              tts: {
                                reps: {
                                  [repKey]: {
                                    language: e.target.value,
                                  },
                                },
                              },
                            });
                          }}
                        />
                        {/* dropdown that sets language code */}
                        <label className="cell">
                          Use other rep for playback
                        </label>
                        <select
                          className="cell"
                          value={ttsConfig?.reps?.[repKey]?.playbackRepId ??
                            ""}
                          onChange={(e) => {
                            cb.modifyPackConfig({
                              tts: {
                                reps: {
                                  [repKey]: {
                                    playbackRepId: e.target.value,
                                  },
                                },
                              },
                            });
                          }}
                        >
                          <option value=""></option>
                          {repKeys.map((otherKey) => (
                            otherKey !== repKey
                              ? (
                                <option value={otherKey}>
                                  {pack.data.reps[otherKey]?.promptText ??
                                    otherKey}
                                </option>
                              )
                              : null
                          ))}
                        </select>
                      </>
                    </div>
                  );
                })}

                <label className="cell">
                  Always Play TTS on Answer
                </label>
                <input
                  type="checkbox"
                  value={"tts-play-on-answer"}
                  checked={ttsConfig.alwaysPlayOnAnswer}
                  onChange={(e) => {
                    cb.modifyPackConfig({
                      tts: {
                        alwaysPlayOnAnswer: e.target.checked,
                      },
                    });
                  }}
                />

                {ttsConfig.alwaysPlayOnAnswer && (
                  <>
                    {/* dropdown that sets language code */}
                    <label className="cell">
                      Rep to play on answer
                    </label>
                    <select
                      className="cell"
                      value={ttsConfig.alwaysPlayOnAnswerRepId ??
                        ""}
                      onChange={(e) => {
                        cb.modifyPackConfig({
                          tts: {
                            alwaysPlayOnAnswerRepId: e.target.value,
                          },
                        });
                      }}
                    >
                      <option value=""></option>
                      {repKeys.map((key) => (
                        <option value={key}>
                          {pack.data.reps[key]?.promptText ??
                            key}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {selectedCols.includes("examples") && (
        <>
          <div className="section">💳 previews/examples</div>
          <select
            className="cell"
            value={pack.extra?.exampleWires?.[0] ?? ""}
            onChange={(e) => {
              // setSelectedWireStr(e.target.value);
              const exampleWires = pack.extra?.exampleWires ?? [];
              exampleWires[0] = e.target.value;

              cb.modifyPackExtra({
                exampleWires: exampleWires,
              });
            }}
          >
            <option value=""></option>
            {allWireStrs.map((wireStr) => (
              <option value={wireStr}>{wireStr}</option>
            ))}
          </select>
          {(pack.extra?.exampleWires?.length || 0) >= 1 && (
            <select
              className="cell"
              value={pack.extra?.exampleWires?.[1] ?? ""}
              onChange={(e) => {
                // setSelectedWireStr(e.target.value);
                const exampleWires = pack.extra?.exampleWires ?? [];
                if (exampleWires.length === 1) {
                  exampleWires.push(e.target.value);
                } else {
                  exampleWires[1] = e.target.value;
                }

                cb.modifyPackExtra({
                  exampleWires,
                });
              }}
            >
              <option value=""></option>
              {allWireStrs.map((wireStr) => (
                <option value={wireStr}>{wireStr}</option>
              ))}
            </select>
          )}
          {(pack.extra?.exampleWires?.length || 0) >= 2 && (
            <select
              className="cell"
              value={pack.extra?.exampleWires?.[2] ?? ""}
              onChange={(e) => {
                // setSelectedWireStr(e.target.value);
                const exampleWires = pack.extra?.exampleWires ?? [];
                if (exampleWires.length === 2) {
                  exampleWires.push(e.target.value);
                } else {
                  exampleWires[2] = e.target.value;
                }

                cb.modifyPackExtra({
                  exampleWires: exampleWires,
                });
              }}
            >
              <option value=""></option>
              {allWireStrs.map((wireStr) => (
                <option value={wireStr}>{wireStr}</option>
              ))}
            </select>
          )}
        </>
      )}
      <div className="section">🔗 Connections</div>
      {/* HTML Dropdown that shows " ", ">", "<", "<>" */}
      {repPairs.map((pair) => {
        return (
          <div className="padding" style={{ display: "inline-block" }}>
            {pair[0]}
            <select
              className="dropdown"
              value={pair[1]}
              onChange={(e) => {
                const value = e.target.value;
                cb.setWire([pair[0], value, pair[2]]);
              }}
            >
              <option value=""></option>
              <option value=">">→</option>
              <option value="<">←</option>
              <option value="<>">↔</option>
            </select>
            {pair[2]}
          </div>
        );
      })}
    </>
  );
};
